import { useEffect, useState } from "react";

import { cn } from "@/lib/utils";

interface StorefrontPriceInputProps {
  value: number | null;
  density?: "default" | "compact";
  onCommit: (price: number) => void;
}

/**
 * 쇼핑몰 단가 칸.
 *
 * 타이핑마다 저장하지 않는다 — 「42000」을 치는 도중의 「4」·「42」가 저장되면 그 사이
 * 피드를 읽은 쇼핑몰이 4원짜리를 판다. **Enter 또는 포커스가 떠날 때만** 확정한다.
 *
 * 값이 비어 있는 것은 «아직 안 판다» 는 뜻이고 0원과 다르다. 그래서 비우는 것으로는
 * 아무것도 보내지 않는다 — 내리는 것은 마스터가 노출을 중지하는 쪽이다.
 */
export function StorefrontPriceInput({
  value,
  density = "default",
  onCommit,
}: StorefrontPriceInputProps) {
  const [draft, setDraft] = useState(value == null ? "" : String(value));
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!editing) setDraft(value == null ? "" : String(value));
  }, [value, editing]);

  const commit = () => {
    setEditing(false);
    const next = Number(draft.trim().replace(/[,\s]/g, ""));
    if (!Number.isInteger(next) || next < 1) {
      setDraft(value == null ? "" : String(value));
      return;
    }
    if (next !== value) onCommit(next);
  };

  return (
    <input
      value={draft}
      inputMode="numeric"
      placeholder="미입력"
      aria-label="쇼핑몰 단가"
      onFocus={() => setEditing(true)}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") {
          setDraft(value == null ? "" : String(value));
          setEditing(false);
          e.currentTarget.blur();
        }
      }}
      className={cn(
        "w-full rounded border border-border bg-background text-right tabular-nums",
        density === "compact" ? "px-1.5 py-1 text-xs" : "px-2 py-1.5 text-sm",
        value == null ? "text-muted-foreground" : "font-semibold",
      )}
    />
  );
}
